<template>
  <v-dialog v-model="opened" :persistent="persistent">
    <v-card class="basic-modal" elevation="4">
      <v-card-title class="d-flex justify-space-between">
        <h6 class="heading--text d-flex align-center">
          <v-icon size="24" class="mr-4" color="info"> $wordpress </v-icon>
          <span> {{ $t("heading.instance.ssoSingleLogin.title") }}</span>
        </h6>
        <v-btn v-if="!submitting" icon x-small @click="opened = false">
          <v-icon size="24">$close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div>
          <v-form v-if="this.wpUsers.length > 1" class="d-flex flex-column">
            <div class="d-flex flex-column">
              <label class="base--text mb-2">
                {{ $t("form.label.loginAs") }}
                <form-help-icon
                  :text="$t('tooltip.instance.ssoLogin.field.user')"
                />
              </label>
              <div class="last-child">
                <v-select
                  outlined
                  v-model="wpUser"
                  :items="wpUsers"
                  item-text="label"
                  hide-details="auto"
                  item-value="value"
                  dense
                  append-icon="$dropdown"
                  :menu-props="{
                    offsetY: true,
                  }"
                />
              </div>
            </div>
          </v-form>
          <p v-if="message" class="mb-4 p-2">
            {{ message }}
          </p>
          <div v-if="alert" class="warning-box my-5">
            <v-icon color="warning" size="20" class="mr-2">$alertcircle</v-icon>
            <span class="warning--text">{{ alert }}</span>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex flex-wrap pb-4">
        <div v-if="!showLoginButton && showGeneratePreviewLinkButton" class="w-100 pb-2">
          <v-row>
            <v-col>
              <v-btn
                :text="false"
                large
                block
                elevation="0"
                class="primary"
                @click="!submitting && generateSsoPreviewUrl()"
                :loading="submitting"
              >
                <template v-slot:loading>
                  <loader />
                </template>
                <span class="p-2 font-weight-600">
                  {{ $t("button.loginWithTempUrl") }}
                </span>
              </v-btn>
            </v-col>
            <v-col>
              <v-btn
                :text="false"
                large
                block
                elevation="0"
                class="gray"
                @click="!submitting && submit()"
                :loading="submitting"
              >
                <template v-slot:loading>
                  <loader />
                </template>
                <span class="p-2 font-weight-600">
                  {{ $t("button.loginDirectly") }}
                </span>
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <div v-else-if="!showLoginButton && showGenerateLinkButton" class="w-100 pb-2">
          <v-btn
            :text="false"
            large
            block
            elevation="0"
            class="primary"
            @click="!submitting && submit()"
            :loading="submitting"
          >
            <template v-slot:loading>
              <loader />
            </template>
            <span class="p-2 font-weight-600">
              {{ $t("button.generateSsoLink") }}
            </span>
          </v-btn>
        </div>
        <div v-if="showLoginButton" class="w-100 pb-2">
          <v-btn
            :text="false"
            large
            block
            elevation="0"
            class="primary"
            @click="
              window.open(loginUrl, '_blank');
              close();
            "
            :loading="submitting"
          >
            <template v-slot:loading>
              <loader />
            </template>
            <span class="p-2 font-weight-600">
              {{ $t("button.ssoLogin") }}
            </span>
          </v-btn>
        </div>
        <div class="w-100 pb-2 cancel-btn">
          <v-btn
            :text="true"
            large
            block
            elevation="0"
            class="nornal"
            @click="!submitting && close()"
          >
            <template v-slot:loading>
              <loader />
            </template>
            <span class="p-2 font-weight-bold gray--text text--darken-1">
              {{ $t("button.cancel") }}
            </span>
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Api from "@/apis/Api";
import { skipARecordVerification } from "../../apis/CdnDns";
import FormHelpIcon from "../FormHelpIcon.vue";

export default {
  components: {
    FormHelpIcon,
  },
  data() {
    return {
      opened: false,
      persistent: false,
      submitting: false,
      submitSuccess: false,
      wpUsers: [],
      wpUser: null,
      loginUrl: "",
      showLoginButton: false,
      checkingDns: false,
      instance: null,
      window,
    };
  },
  computed: {
    isAppSingleServer() {
      return !!this.$store.state.home.app.single_server?.active;
    },
    showGeneratePreviewLinkButton() {
      return this.dnsMismatchDetected &&
        (this.isAppSingleServer ||
          this.$store.state.home.app.integrations
            .preview_site_provider_enabled);
    },
    showGenerateLinkButton() {
      return !this.showGeneratePreviewLinkButton &&
        (this.wpUsers.length > 1 || this.dnsMismatchDetected);
    },
    dnsMismatchDetected() {
      return this.instance && this.instance.getDnsResolveStatus() === false;
    },
    message() {
      if (this.submitting) {
        return this.$t("heading.instance.ssoSingleLogin.message");
      }
      if (this.checkingDns) {
        return this.$t("heading.instance.ssoSingleLogin.message");
      }
      if (this.submitSuccess) {
        return this.$t("notification.instance.modal.ssoLogin.success");
      }
      return "";
    },
    alert() {
      if (this.dnsMismatchDetected) {
        return this.$t("notification.instance.modal.ssoLogin.dnsMismatchDetected");
      }
      return "";
    },
  },
  methods: {
    async open({ application, options }) {
      this.submitSuccess = false;
      this.loginUrl = "";
      this.showLoginButton = false;
      this.instance = application;
      this.wpUsers = options;
      if (this.wpUsers.length > 0) {
        this.wpUser = this.wpUsers[0].value;
      }
      this.opened = true;
      await this.ensureDnsResolveStatus();
      if (!this.opened) {
        return;
      }
      if (this.wpUsers.length === 1 && !this.alert) {
        this.submit();
      }
    },
    close() {
      this.opened = false;
    },
    async ensureDnsResolveStatus() {
      if (
        !this.instance ||
        this.instance.getDnsResolveStatus() !== null ||
        this.instance.hasIpAddressInUrl()
      ) {
        return;
      }

      this.checkingDns = true;
      try {
        const resolvedCorrectly = await Api.dns().verifyDomainResolving(
          this.instance.domain,
          this.instance.onboarding_subdomain,
          this.instance.dns_settings,
          this.instance.service_has_dns,
          this.instance.getDnsIpv4Addresses(),
          this.instance.dns_nameservers,
          this.instance.host_c_names ?? [],
          this.instance.domain_zone_details?.configuration_records ?? [],
          false,
          skipARecordVerification(this.instance)
        );
        this.instance.setDnsResolveStatus(resolvedCorrectly);
      } catch {
        this.instance.setDnsResolveStatus(false);
      } finally {
        this.checkingDns = false;
      }
    },
    submit() {
      this.persistent = true;
      this.submitting = true;
      Api.post(`/instances/${this.instance.id}/sso-url`, { user: this.wpUser })
        .then((response) => {
          this.submitSuccess = true;
          let newWindow = window.open(response.data.data.sso_url, "_blank");
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed == "undefined"
          ) {
            this.loginUrl = response.data.data.sso_url;
            this.showLoginButton = true;
          } else {
            this.close();
          }
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.persistent = false;
          this.submitting = false;
        });
    },
    generateSsoPreviewUrl() {
      this.persistent = true;
      this.submitting = true;
      Api.post(`/instances/${this.instance.id}/sso-preview-url`, { user: this.wpUser })
        .then((response) => {
          this.submitSuccess = true;
          let newWindow = window.open(response.data.data.sso_url, "_blank");
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed == "undefined"
          ) {
            this.loginUrl = response.data.data.sso_url;
            this.showLoginButton = true;
          } else {
            this.close();
          }
        })
        .catch((error) => {
          this.$store.dispatch("addAlert", {
            success: false,
            errorMessage: Api.getErrorMessage(error),
          });
        })
        .finally(() => {
          this.persistent = false;
          this.submitting = false;
        });
    },
  },
};
</script>

<style src="./BasicModal.scss" lang="scss" scoped/>
